import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Wrench, Flame, Hammer, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/booking', label: 'Book Now' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="navbar"
        className={`navbar transition-all duration-300 ${
          isScrolled ? 'navbar-scrolled py-3' : 'py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#008F7A] flex items-center justify-center">
              <span className="font-heading text-black font-black text-lg">O</span>
            </div>
            <span className="font-heading text-xl font-bold tracking-wider">
              <span className="text-[#00F0FF]">OMNI</span>
              <span className="text-white">BEAST</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-[#00F0FF] glow-text'
                    : 'text-gray-300 hover:text-[#00F0FF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/booking">
              <Button
                data-testid="nav-cta-button"
                className="bg-gradient-to-r from-[#00F0FF] to-[#008F7A] text-black font-semibold px-6 hover:opacity-90 transition-opacity"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              className="absolute top-5 right-5 text-white p-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X size={24} />
            </button>

            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#008F7A] flex items-center justify-center">
                <span className="font-heading text-black font-black text-xl">O</span>
              </div>
              <span className="font-heading text-2xl font-bold tracking-wider">
                <span className="text-[#00F0FF]">OMNI</span>
                <span className="text-white">BEAST</span>
              </span>
            </div>

            {navLinks.map((link, index) => (
              <motion.div
                key={link.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={link.to}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`text-2xl font-heading tracking-wide ${
                    isActive(link.to) ? 'text-[#00F0FF]' : 'text-white'
                  }`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-4"
            >
              <Link to="/booking">
                <Button
                  data-testid="mobile-cta-button"
                  className="bg-gradient-to-r from-[#00F0FF] to-[#008F7A] text-black font-semibold px-8 py-3 text-lg"
                >
                  Get Quote
                </Button>
              </Link>
            </motion.div>

            {/* Service Icons */}
            <div className="flex gap-6 mt-12 opacity-50">
              <Flame className="w-6 h-6 text-[#00F0FF]" />
              <Wrench className="w-6 h-6 text-[#00F0FF]" />
              <Hammer className="w-6 h-6 text-[#00F0FF]" />
              <Sparkles className="w-6 h-6 text-[#00F0FF]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
